/**
 * Typed client for `/api/v1/proofs` — per-user proof persistence.
 *
 * Mirrors the server's [`Proof`](../../../crates/gi-api-server/src/proof_store.rs)
 * shape one-for-one. Auth is handled by `apiFetch`: the live
 * Supabase JWT is forwarded automatically, and the server scopes
 * every read/write to that user's `sub`.
 */

import { apiFetch } from './http';

/** Recognised proof kinds. The server stores any non-empty string,
 *  so adding a new kind on the UI side does not require an API
 *  release. */
export type ProofKind =
  | 'file'
  | 'idea'
  | 'invoice'
  | 'contract'
  | 'memo'
  | 'document';

/** One persisted proof — wire shape returned by the API. Field-for-
 *  field mirror of the Rust `Proof` struct + the legacy
 *  `proofsStore` shape, so call sites that previously read from
 *  the Zustand slice work unchanged. */
export interface Proof {
  id: string;
  kind: ProofKind;
  name: string;
  hash: string;
  timestamp: string;
  signer: string;
  version: string;
  bytes?: number;
}

interface ListProofsResponse {
  proofs: Proof[];
}

interface CreateProofResponse {
  id: string;
}

/** `GET /api/v1/proofs` — newest-first, capped at the server's
 *  `MAX_LIST` (50). */
export async function listProofs(): Promise<Proof[]> {
  const res = await apiFetch<ListProofsResponse>('/proofs');
  return res.proofs ?? [];
}

/** `GET /api/v1/proofs/{id}`. */
export async function getProof(id: string): Promise<Proof> {
  return apiFetch<Proof>(`/proofs/${encodeURIComponent(id)}`);
}

/** `POST /api/v1/proofs` — server returns 409 if `id` already
 *  exists for this user. The mutation surface in `useProofs`
 *  catches that and falls through to a refresh. */
export async function createProof(proof: Proof): Promise<{ id: string }> {
  return apiFetch<CreateProofResponse>('/proofs', {
    method: 'POST',
    body: proof,
  });
}

/** `DELETE /api/v1/proofs/{id}` — server returns 204 on success,
 *  404 if the row was already gone. */
export async function deleteProof(id: string): Promise<void> {
  await apiFetch<void>(`/proofs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
